/**
 * useCurrentCompany
 *
 * Thin wrapper around the `/me/current-company` endpoint. Any surface that
 * needs to know which company the operator is presenting reads it here so
 * we have exactly one query key + one switch mutation across the app.
 *
 * The active company lives on the SPA session (per-browser cookie), NOT on
 * the user row — that's what lets a marketing operator open two browsers
 * signed in as the same account and pitch pharma to one prospect and
 * electric motors to another simultaneously.
 *
 * Shape returned by the backend (see api/app/Http/Controllers/CurrentCompanyController):
 *   { data: { id, code, name, industry_key, industry_label } | null }
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/shared/api/client';

export interface CurrentCompany {
  id: number;
  code: string;
  name: string;
  industry_key: string | null;
  industry_label: string | null;
  gstin?: string | null;
  currency?: string;
}

export const CURRENT_COMPANY_KEY = ['me/current-company'] as const;
export const AVAILABLE_COMPANIES_KEY = ['companies/available'] as const;

interface ShowEnvelope {
  data: CurrentCompany | null;
}

interface AvailableEnvelope {
  data: CurrentCompany[];
}

/**
 * The company the current SPA session is presenting.
 *
 * `company` is `null` in three cases: (a) still loading, (b) the operator
 * has no accessible companies at all, or (c) the endpoint failed. Callers
 * that need to distinguish "loading" from "empty" should also inspect
 * `isLoading`.
 */
export function useCurrentCompany(): {
  company: CurrentCompany | null;
  isLoading: boolean;
  switchCompany: (companyId: number) => Promise<void>;
  isSwitching: boolean;
} {
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: CURRENT_COMPANY_KEY,
    queryFn: async (): Promise<CurrentCompany | null> => {
      const { data } = await api.get<ShowEnvelope>('/api/v1/me/current-company');
      return data.data ?? null;
    },
    // The session cookie is the source of truth here; no need for aggressive
    // refetching. A window focus refetch keeps us honest if the operator
    // switched in the other tab.
    staleTime: 30_000,
  });

  const m = useMutation({
    mutationFn: async (companyId: number): Promise<void> => {
      await api.post('/api/v1/me/current-company', { company_id: companyId });
    },
    onSuccess: async () => {
      // The current-company query needs a fresh read; every OTHER query is
      // now potentially scoped to a different company_id, so blow the
      // cache and let each surface re-fetch on demand.
      await qc.invalidateQueries();
    },
    onError: (err) => {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e?.response?.data?.message ?? 'Could not switch company.');
    },
  });

  return {
    company: q.data ?? null,
    isLoading: q.isLoading,
    switchCompany: async (companyId: number) => {
      await m.mutateAsync(companyId);
    },
    isSwitching: m.isPending,
  };
}

/**
 * Every company the current user can switch to. Grouped by the caller —
 * the topbar's picker groups by `industry_key`.
 */
export function useAvailableCompanies() {
  return useQuery({
    queryKey: AVAILABLE_COMPANIES_KEY,
    queryFn: async (): Promise<CurrentCompany[]> => {
      const { data } = await api.get<AvailableEnvelope>('/api/v1/companies/available');
      return data.data ?? [];
    },
    staleTime: 60_000,
  });
}
